// RemoteConfigMonitor.jsx
import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import Redirect from '../container/Redirect.js';

import { BindEmail } from '../resource/authEntryType.js';

const isServer = typeof window === 'undefined';

export const RemoteConfigMonitor = ({
  hasToken,
  isRegisterJustCompleted,
  isConfigureRemoteConfigMerged,
  isPresenceClientRemoteConfigMerged,
  isPresenceUserRemoteConfigMerged,
  onloadRedirectUrl,
  shouldOpenAgeVerifyModal,
  shouldAgeVerify,
  shouldOpenBindEmailModal,
  openVerifyFreeUserModal,
  redirectRecord,
  updateRedirectRecord,
  openBindEmailModal,
}) => {
  const hasRedirected = useRef(false);

  // redirect user by remote config, ex: redirect with utm_content
  const { pathname } = useLocation();
  const redirectTarget = useMemo(() => {
    try {
      if (!onloadRedirectUrl) {
        return null;
      }

      const url = new URL(onloadRedirectUrl);
      const shouldRedirect =
        url.pathname !== pathname &&
        ((isServer && isConfigureRemoteConfigMerged) ||
          (hasToken
            ? isPresenceUserRemoteConfigMerged
            : isPresenceClientRemoteConfigMerged));
      if (!shouldRedirect) {
        return null;
      }

      let redirectUrl = onloadRedirectUrl;
      if (url.hostname === 'swag.live') redirectUrl = url.pathname;

      if (redirectRecord[redirectUrl]) return null;

      updateRedirectRecord({ [redirectUrl]: true });
      return redirectUrl;
    } catch (error) {
      return null;
    }
  }, [
    hasToken,
    isConfigureRemoteConfigMerged,
    isPresenceClientRemoteConfigMerged,
    isPresenceUserRemoteConfigMerged,
    onloadRedirectUrl,
    pathname,
    redirectRecord,
    updateRedirectRecord,
  ]);

  useEffect(() => {
    if (
      isRegisterJustCompleted &&
      isPresenceUserRemoteConfigMerged &&
      shouldOpenBindEmailModal
    ) {
      openBindEmailModal({
        entry: BindEmail.REGISTER,
        canClose: true,
      });
    }
  }, [
    isPresenceUserRemoteConfigMerged,
    isRegisterJustCompleted,
    shouldOpenBindEmailModal,
    openBindEmailModal,
  ]);

  // extra with email A/B testing (settle down to always yes)
  useEffect(() => {
    if (
      !isRegisterJustCompleted ||
      !isPresenceUserRemoteConfigMerged ||
      !shouldOpenAgeVerifyModal ||
      !shouldAgeVerify
    )
      return;

    openVerifyFreeUserModal();
  }, [
    isRegisterJustCompleted,
    isPresenceUserRemoteConfigMerged,
    shouldOpenAgeVerifyModal,
    shouldAgeVerify,
    openVerifyFreeUserModal,
  ]);

  if (redirectTarget && !hasRedirected.current) {
    // to prevent infinite loop on client side
    hasRedirected.current = true;
    return <Redirect to={redirectTarget} status={302} />;
  }
  return null;
};

RemoteConfigMonitor.propTypes = {
  hasToken: PropTypes.bool,
  isRegisterJustCompleted: PropTypes.bool,
  isConfigureRemoteConfigMerged: PropTypes.bool,
  isPresenceClientRemoteConfigMerged: PropTypes.bool,
  isPresenceUserRemoteConfigMerged: PropTypes.bool,
  onloadRedirectUrl: PropTypes.string,
  shouldOpenAgeVerifyModal: PropTypes.bool,
  shouldAgeVerify: PropTypes.bool,
  shouldOpenBindEmailModal: PropTypes.bool,
  openVerifyFreeUserModal: PropTypes.func,
  redirectRecord: PropTypes.object,
  updateRedirectRecord: PropTypes.func,
  openBindEmailModal: PropTypes.func,
};

RemoteConfigMonitor.defaultProps = {
  hasToken: false,
  isRegisterJustCompleted: false,
  isConfigureRemoteConfigMerged: false,
  isPresenceClientRemoteConfigMerged: false,
  isPresenceUserRemoteConfigMerged: false,
  onloadRedirectUrl: '',
  shouldOpenAgeVerifyModal: false,
  shouldAgeVerify: false,
  shouldOpenBindEmailModal: false,
  openVerifyFreeUserModal: () => null,
  redirectRecord: {},
  updateRedirectRecord: () => null,
  openBindEmailModal: () => null,
};

export default RemoteConfigMonitor;
